import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SearchBar from './SearchBar';
import { db } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar: React.FC = () => {
  const { user, signOut, loading } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userIcon, setUserIcon] = useState<string>('🌙');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUserIcon = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserIcon(userDoc.data().icon || '🌙');
        }
      } catch (error) {
        console.error('Error fetching user icon:', error);
      }
    };

    fetchUserIcon();
  }, [user]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    try {
      setIsLoggingOut(true);
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/dreams/map', label: 'Constellation', icon: '✧' },
    { path: '/search', label: 'Search', icon: '🔍' },
    ...(user ? [{ path: `/profile/${user.uid}`, label: 'Profile', icon: userIcon }] : []),
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-dream-900/95 backdrop-blur-sm border-b border-dream-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <span className="text-dream-100 text-xl font-semibold">DriftRealm</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2
                  ${isActive(item.path)
                    ? 'bg-dream-800 text-dream-100'
                    : 'text-dream-300 hover:bg-dream-800/50 hover:text-dream-100'
                  }`}
              >
                {item.icon && <span className="text-lg">{item.icon}</span>}
                {item.label}
              </Link>
            ))}
            {user ? (
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                         text-dream-300 hover:bg-dream-800/50 hover:text-dream-100
                         ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoggingOut ? '...' : 'Logout'}
              </button>
            ) : (
              <button
                onClick={handleLogin}
                className="px-3 py-2 rounded-md text-sm font-medium transition-colors
                         bg-dream-500 text-dream-100 hover:bg-dream-400"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-dream-300
                       hover:text-dream-100 hover:bg-dream-800 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-inset focus:ring-dream-500"
            >
              <span className="sr-only">Open main menu</span>
              {!isMenuOpen ? (
                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-dream-800"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 bg-dream-900/95 backdrop-blur-sm">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium transition-colors
                    ${isActive(item.path)
                      ? 'bg-dream-800 text-dream-100'
                      : 'text-dream-300 hover:bg-dream-800/50 hover:text-dream-100'
                    }`}
                >
                  {item.icon && <span className="text-lg">{item.icon}</span>}
                  {item.label}
                </Link>
              ))}
              {user ? (
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  disabled={isLoggingOut}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium
                           text-dream-300 hover:bg-dream-800/50 hover:text-dream-100
                           transition-colors"
                >
                  {isLoggingOut ? '...' : 'Logout'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleLogin();
                    setIsMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium
                           bg-dream-500 text-dream-100 hover:bg-dream-400
                           transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar; 